/*
Copyright 2019 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License.
*/

import d3 from './d3';
import * as Leo from '@adobe/leonardo-contrast-colors';
const BezierEasing = require('./BezierEasing')
import {
  sortRatios,
  createRatioInput
} from './ratios';
import {
  round
} from './utils';

let customBezier = [0, 0, 1, 1];
const easings = {
  linear:  [0, 0, 1, 1],
  custom: customBezier,
  easeInSine: [0.12, 0, 0.39, 0],
  easeOutSine: [0.61, 1, 0.88, 1],
  easeInOutSine: [0.37, 0, 0.63, 1],
  easeInQuad: [0.11, 0, 0.5, 0],
  easeOutQuad: [0.5, 1, 0.89, 1],
  easeInOutQuad: [0.45, 0, 0.55, 1],
  easeInCubic: [0.32, 0, 0.67, 0],
  easeOutCubic: [0.33, 1, 0.68, 1],
  easeInOutCubic: [0.65, 0, 0.35, 1],
  easeInQuart: [0.5, 0, 0.75, 0],
  easeOutQuart: [0.25, 1, 0.5, 1],
  easeInOutQuart: [0.76, 0, 0.24, 1]
};
const bezierOutput = document.querySelector('.bezierOutput');
const bezierEl = document.getElementById('bulkLightnessColorsBezier');
const svg = document.getElementById('editor');
const bezierStartEl = document.getElementById('bezierStart');
const bezierEndEl = document.getElementById('bezierEnd');
const stopsEl = document.getElementById('stops');
const contrastsEl = document.getElementById('bulkLightnessColors');


function CurveEditor(svg, opts) {
	if(!svg.select) {
		svg = d3.select(svg);
	}
	opts.axis = opts.axis || {};

	var self = this;
	this.pointSize = opts.pointSize || 5;
	this.svg = svg;
	this.dispatch = d3.dispatch('change');
	this.scale = opts.axis.scale || 1;
	this.precision = opts.precision || 4;

	if(opts.size) {
		this.size = opts.size;
	}
	else {
		var viewbox = svg.attr('viewBox');
		if(viewbox) {
			viewbox = viewbox.split(' ');
			this.size = [
				parseInt(viewbox[2]),
				parseInt(viewbox[3])
			];
		}
		else {
			this.size = [100, 100];
		}
	}
	this.padding = opts.axis.padding || 0.2 * this.size[0];
	this.points = opts.points || [
		[0, 0],
		[0, this.size[1]],
		[this.size[1], 0],
		[this.size[0], this.size[1]]
	];

	svg
		.classed('curve-editor', true)
		.attr(
			'viewBox',
			'10 10 '.concat(
				this.size[0] + 2 * (this.padding / 2),  ' ',
				this.size[1] + 2 * (this.padding / 2))
		);

	var xScale = d3.scaleLinear()
		.domain([0, this.size[0] / this.scale])
		.range([0, this.size[0]]);
  var yScale = d3.scaleLinear()
		.domain([0, this.size[1] / this.scale])
		.range([this.size[1], 0]);
	var xAxis = d3.axisBottom(xScale);
	var yAxis = d3.axisLeft(yScale);
	if(opts.axis.format) {
		xAxis.tickFormat(opts.axis.format);
		yAxis.tickFormat(opts.axis.format);
	}
	var labels = opts.axis.label || ['X', 'Y'];
	svg.append('g')
		.attr('transform', translate(
			this.padding, this.size[1] + this.padding
		))
		.call(xAxis)
		.append('text')
		.attr('transform', translate(
			this.size[0] + this.padding / 2,
			this.padding / 4
		))
		.text(labels[0]);
	svg.append('g')
		.attr('transform', translate(
			this.padding, this.padding
		))
		.call(yAxis)
		.append('text')
		.attr('text-anchor', 'middle')
		.attr('transform', translate(
			0, -this.padding / 4
		))
		.text(labels[1]);

	var g = svg.append('g').attr(
		'transform',
		translate(
			this.padding, this.size[1] + this.padding
		) + 'scale(1,-1)'
	);

	this.container = g.node();

	var links = [[0, 1], [1, 2], [2, 3], [0, 3]];
	links = g.selectAll('.line')
		.data(links).enter()
		.append('path')
		.classed('line', true)
		.classed('curve', function(d) {
			return self._isCurve(d);
		});

	var points = g.selectAll('.point')
		.data(this.points).enter()
		.append('g')
		.classed('point', true)
		.classed('start', function(d, i) {
			return i === 0;
		})
		.classed('end', function(d, i) {
			return i === self.points.length - 1;
		});
	points.append('circle')
		.attr('cx', 0).attr('cy', 0)
		.attr('r', this.pointSize);
	points.append('text')
		.attr('dx', this.pointSize * 1.25)
		.attr('dy', this.pointSize * 0.25)
		.text(function(d, i) { return 'P' + i; });

	var drag = d3.drag()
		.on('drag', function(d) {
			var pos = touchOrMouse(self.container);
			d[0] = clamp(pos[0], 0, self.size[0]) / self.scale;
			d[1] = clamp(pos[1], 0, self.size[1]) / self.scale;
			self.update();
		});
	points.call(drag);

	return this.update();
}

CurveEditor.prototype.on = function on(event, handler) {
	this.dispatch.on(event, handler);
	return this;
};

CurveEditor.prototype.update = function update() {
	var self = this;
	this.svg.selectAll('.point')
		.attr('transform', function(d) {
			return translate(d[0] * self.scale, d[1] * self.scale);
		});
	this.svg.selectAll('.line')
		.attr('d', function(d) {
			return self._path(d);
		});
	this.dispatch.call('change', this);
	return this;
};

CurveEditor.prototype.path = (function() {
	var pathData = [
		'M',,,
		'C',,,,,,
	];
	return function path(scale, precision) {
		function coord(x) {
			return (x * scale).toFixed(precision);
		}
		scale = scale || this.scale;
		precision = precision || this.precision;
		for(var i = 0, j = 1; i < this.points.length; ++i) {
			if(i === 1) {
				++j;
			}
			pathData[j++] = coord(this.points[i][0]);
			pathData[j++] = coord(this.points[i][1]);
		}
		return pathData.join(' ');
	}
})();

CurveEditor.prototype._isCurve = function _isCurve(d) {
	return d[1] - d[0] === 3;
};

CurveEditor.prototype._path = function _path(d) {
	if(this._isCurve(d)) {
		return this.path();
	}
	var self = this;
	function coord(x) {
		return (x * self.scale).toFixed(self.precision);
	}
	var start = this.points[d[0]];
	var end = this.points[d[1]];
	return 'M'.concat(
		coord(start[0]), ',', coord(start[1]),
		'L', coord(end[0]), ',', coord(end[1])
	);
};

function clamp(x, min, max) {
	return Math.max(min, Math.min(x, max));
}

function translate(x, y) {
	return 'translate('.concat(x, ',', y, ')');
}

function touchOrMouse(container) {
  easings['custom'] = JSON.parse("[" + bezierOutput.textContent + "]");
  bezierEl.value = 'custom';
  const stopsValue = stopsEl.value < 2 ? 2 : Number(stopsEl.value);
  const bezierStartValue = (bezierStartEl.value.length == 0 || bezierStartEl.value == 0 || Number(bezierStartEl.value) > Number(bezierEndEl.value)) ? 1 : Number(bezierStartEl.value);
  const bezierEndValue = (bezierEndEl.value.length == 0 || bezierEndEl.value == 0 || Number(bezierStartEl.value) > Number(bezierEndEl.value)) ? 21 : Number(bezierEndEl.value);
  const bezierValue = bezierEl.value;
  contrastsEl.value = generateCubicBezierValues(bezierStartValue, bezierEndValue, stopsValue, [...easings[bezierValue]]);
	return d3.touch(container) || d3.mouse(container);
}

function init(points) {
  var p = [[points[0], points[1]], [points[2], points[3]]]
	var options = {
		size: [100, 100],
		points: [[0, 0], p[0], p[1], [1, 1]],
		pointSize: 2.5,
		precision: 2,
		axis: {
			scale: 100,
			padding: 20,
			label: ['X', 'Y'],
			format: function format(x) {
				return x < 0.99 ? x.toFixed(1).substr(1) : '1';
			}
		}
	};
	var editor = new CurveEditor('#editor', options);

	function update() {
		bezierOutput.textContent = editor.path(1).substring(editor.path(1).indexOf('C')+2, 33).split(' ').join(', ');
	}

	editor.on('change', update);
	update();
}
document.addEventListener("DOMContentLoaded", function(event) {
  bezierOutput.textContent = bezierEl.value + ': '+ easings[bezierEl.value];
  init(easings.linear);
});


function addRatioValue(value) {
  // createHtmlElement
  createRatioInput(value);
}

const generateCubicBezierValues = (startValue, endValue, steps, [x1, y1, x2, y2]) => {
	const easing = BezierEasing(x1, y1, x2, y2);

  const result = [];
  
  for (let i = 0; i < steps; i++) {
  	const currentValueRelative = i / (steps - 1);
    const currentValueEasing = easing(currentValueRelative);
    const currentValueAbsolute = currentValueEasing * (endValue - startValue) + startValue;  
  
  	result.push((currentValueAbsolute).toFixed(2));
  }
  
  return result.join('; ');
}

function convertBezierToSteps() {
  const stopsValue = stopsEl.value < 2 ? 2 : Number(stopsEl.value);
  const bezierStartValue = (bezierStartEl.value.length == 0 || bezierStartEl.value == 0 || Number(bezierStartEl.value) > Number(bezierEndEl.value)) ? 1 : Number(bezierStartEl.value);
  const bezierEndValue = (bezierEndEl.value.length == 0 || bezierEndEl.value == 0 || Number(bezierStartEl.value) > Number(bezierEndEl.value)) ? 21 : Number(bezierEndEl.value);
  const bezierValue = bezierEl.value;

  bezierOutput.textContent = bezierValue + ': '+ easings[bezierValue];
  svg.innerHTML = ""
  init(easings[bezierValue])
  let finalSteps = generateCubicBezierValues(bezierStartValue, bezierEndValue, stopsValue, [...easings[bezierValue]]);
  contrastsEl.value = finalSteps
}

function addRatios(ratios) {
  //delete all contrast steps
  let ratioItems = document.getElementsByClassName('ratio-Item');
  [...ratioItems].forEach((el) => {
    el.querySelector('button').click()
  });

  ratios.forEach((ratio) => {
    addRatioValue(ratio)
  })

  let ratioFields = document.getElementsByClassName('ratio-Field');
  for(let i = 0; i < ratioFields.length; i++) {
    ratioFields[i].dispatchEvent(new Event("input"));
  }
}


function addLightnessBulk(e) {
  let dialog = document.getElementById('addBulkLightnessDialog');
  dialog.classList.add("is-open");

  document.getElementById('dialogOverlay').style.display = 'block';
}

function cancelLightnessBulk() {
  let dialog = document.getElementById('addBulkLightnessDialog');
  dialog.classList.remove("is-open");

  document.getElementById('dialogOverlay').style.display = 'none';
}

function bulkLightnessInput(e) {
  let bulkInputs = document.getElementById('bulkLightnessColors');
  let bulkValues = bulkInputs.value.replace(/\r\n/g,";").replace(" ", "").replace(/['\/]/g, "").replace(/["\/]/g, "").replace(/ /g,'').split(";");

  addRatios(bulkValues);
  sortRatios();

  // Hide dialog
  cancelLightnessBulk();
  // Run colorinput
  themeUpdateParams();

  // clear inputs on close
  bulkInputs.value = " ";
}

window.addLightnessBulk = addLightnessBulk;
window.cancelLightnessBulk = cancelLightnessBulk;
window.bulkLightnessInput = bulkLightnessInput;
window.convertBezierToSteps = convertBezierToSteps;

module.exports = {
  addLightnessBulk,
  bulkLightnessInput,
  cancelLightnessBulk,
  convertBezierToSteps
}